import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, Pencil, UserPlus, Trash2 } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { MultiSelect } from "@/components/MultiSelect";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const ROLE_LABELS = {
  utilizzatore: "Utilizzatore",
  approvatore: "Approvatore",
  admin: "Amministratore",
};
const ROLE_STYLE = {
  utilizzatore: "border-slate-200 bg-slate-50 text-slate-600",
  approvatore: "border-amber-200 bg-amber-50 text-amber-700",
  admin: "border-blue-200 bg-blue-50 text-[#002FA7]",
};
const NONE = "none";
const emptyCreate = { name: "", email: "", password: "", role: "utilizzatore", fuel_card_ids: [], cost_per_hour: "", hourly_costs: {}, fuel_creates_rapportino: true, activity_type_id: "none" };
const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const numHC = (o) => Object.fromEntries(Object.entries(o || {}).map(([k, v]) => [k, parseFloat(v) || 0]));

export default function Users() {
  const [items, setItems] = useState([]);
  const [cards, setCards] = useState([]);
  const [hourTypes, setHourTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ fuel_card_ids: [], role: "utilizzatore" });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const { user: currentUser } = useAuth();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);

  const load = () => {
    api.get("/users").then((r) => setItems(r.data));
    api.get("/fuel-cards").then((r) => setCards(r.data));
    api.get("/hour-types").then((r) => setHourTypes(r.data));
    api.get("/activity-types").then((r) => setActivityTypes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ fuel_card_ids: u.fuel_card_ids || [], role: u.role, password: "", cost_per_hour: u.cost_per_hour ?? "", hourly_costs: u.hourly_costs || {}, fuel_creates_rapportino: u.fuel_creates_rapportino !== false, activity_type_id: u.activity_type_id || "none" });
  };

  const saveEdit = async () => {
    if (editForm.password && editForm.password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    try {
      const payload = {
        role: editForm.role,
        fuel_card_ids: editForm.fuel_card_ids || [],
        cost_per_hour: parseFloat(editForm.cost_per_hour) || 0,
        hourly_costs: numHC(editForm.hourly_costs),
        fuel_creates_rapportino: editForm.fuel_creates_rapportino,
        activity_type_id: editForm.activity_type_id === "none" ? "" : editForm.activity_type_id,
      };
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/users/${editing.id}`, payload);
      toast.success(editForm.password ? "Utente aggiornato e password reimpostata" : "Utente aggiornato");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const createUser = async () => {
    try {
      await api.post("/users", {
        name: createForm.name,
        email: createForm.email,
        password: createForm.password,
        role: createForm.role,
        fuel_card_ids: createForm.fuel_card_ids || [],
        cost_per_hour: parseFloat(createForm.cost_per_hour) || 0,
        hourly_costs: numHC(createForm.hourly_costs),
        fuel_creates_rapportino: createForm.fuel_creates_rapportino,
        activity_type_id: createForm.activity_type_id === "none" ? "" : createForm.activity_type_id,
      });
      toast.success("Utente creato");
      setCreateOpen(false);
      setCreateForm(emptyCreate);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Utente eliminato");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">
            Dipendenti & Carte
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Crea gli utenti, assegna il ruolo e la carta carburante.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="add-user-btn">
              <UserPlus className="mr-2 h-4 w-4" /> Nuovo utente
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Nuovo utente</DialogTitle>
              <DialogDescription>Inserisci i dati di accesso, il ruolo e (opzionale) la carta carburante.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="cu-name">Nome e cognome</Label>
                <Input id="cu-name" data-testid="create-name-input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="mt-1.5" placeholder="Mario Rossi" />
              </div>
              <div>
                <Label htmlFor="cu-email">Email</Label>
                <Input id="cu-email" type="email" data-testid="create-email-input" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="mt-1.5" placeholder="nome@azienda.it" />
              </div>
              <div>
                <Label htmlFor="cu-pass">Password</Label>
                <Input id="cu-pass" type="text" autoCapitalize="none" autoCorrect="off" autoComplete="off" spellCheck={false} data-testid="create-password-input" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="mt-1.5" placeholder="min. 6 caratteri" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ruolo</Label>
                  <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="create-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="utilizzatore">Utilizzatore</SelectItem>
                      <SelectItem value="approvatore">Approvatore</SelectItem>
                      <SelectItem value="admin">Amministratore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Carte carburante</Label>
                  <div className="mt-1.5">
                    <MultiSelect
                      testid="create-card-select"
                      options={cards.map((c) => ({ value: c.id, label: c.label }))}
                      value={createForm.fuel_card_ids}
                      onChange={(v) => setCreateForm({ ...createForm, fuel_card_ids: v })}
                      allLabel="Nessuna carta"
                      placeholder="Cerca carta..."
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label>Costo orario per tipo di ore (€)</Label>
                {hourTypes.length === 0 ? (
                  <Input id="cu-cost" type="number" step="0.01" data-testid="create-cost-input" value={createForm.cost_per_hour} onChange={(e) => setCreateForm({ ...createForm, cost_per_hour: e.target.value })} className="mt-1.5" placeholder="Costo orario di default" />
                ) : (
                  <div className="mt-1.5 space-y-2">
                    {hourTypes.map((h) => (
                      <div key={h.id} className="flex items-center gap-2">
                        <span className="w-32 shrink-0 truncate text-sm text-slate-600">{h.name}</span>
                        <Input type="number" step="0.01" data-testid={`create-cost-${h.id}`} value={createForm.hourly_costs?.[h.id] ?? ""} onChange={(e) => setCreateForm({ ...createForm, hourly_costs: { ...createForm.hourly_costs, [h.id]: e.target.value } })} placeholder="0,00" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Tipo di attività</Label>
                <Select value={createForm.activity_type_id} onValueChange={(v) => setCreateForm({ ...createForm, activity_type_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="create-activity-select"><SelectValue placeholder="Nessuna attività" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna attività</SelectItem>
                    {activityTypes.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`create-activity-option-${a.id}`}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div className="pr-4">
                  <Label className="text-sm">Genera bozza rapportino dal carburante</Label>
                  <p className="mt-0.5 text-xs text-slate-400">Se attivo, le richieste carburante di questo dipendente creano una bozza di rapportino.</p>
                </div>
                <Switch checked={createForm.fuel_creates_rapportino} onCheckedChange={(v) => setCreateForm({ ...createForm, fuel_creates_rapportino: v })} data-testid="create-fuel-draft-switch" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
              <Button onClick={createUser} disabled={!createForm.name || !createForm.email || createForm.password.length < 6} data-testid="save-create-user-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Crea utente</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <DataTable
            data={items}
            testid="users-table"
            rowTestId={(u) => `user-row-${u.id}`}
            searchKeys={["name", "email", "role", "fuel_card"]}
            searchPlaceholder="Cerca dipendente..."
            initialSort={{ key: "name", dir: "asc" }}
            emptyText="Nessun dipendente"
            columns={[
              { key: "name", header: "Nome", sortable: true, className: "font-medium text-slate-900", cell: (u) => u.name },
              { key: "email", header: "Email", sortable: true, className: "text-slate-600", cell: (u) => u.email },
              { key: "role", header: "Ruolo", sortable: true, sortValue: (u) => ROLE_LABELS[u.role] || u.role, cell: (u) => (
                  <Badge variant="outline" className={ROLE_STYLE[u.role] || ROLE_STYLE.utilizzatore}>
                    {ROLE_LABELS[u.role] || u.role}
                  </Badge>
                ) },
              { key: "fuel_card", header: "Carte carburante", sortable: true, cell: (u) => {
                  const labels = (u.fuel_card_ids || []).map((id) => cards.find((c) => c.id === id)?.label).filter(Boolean);
                  return labels.length ? (
                    <span className="flex items-center gap-1.5 text-slate-700">
                      <CreditCard className="h-4 w-4 shrink-0 text-slate-400" /> {labels.join(", ")}
                    </span>
                  ) : (
                    <span className="text-slate-400">Non assegnata</span>
                  );
                } },
              { key: "cost_per_hour", header: "Costo orario", align: "right", sortable: true, className: "text-right text-slate-600", cell: (u) => u.cost_per_hour ? fmtEur(u.cost_per_hour) : <span className="text-slate-300">—</span> },
              { key: "actions", header: "Azioni", align: "right", stacked: true, cell: (u) => (
                  <div className="flex flex-wrap justify-end gap-2 md:flex-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="rounded-md">
                      <Pencil className="mr-2 h-4 w-4" /> Modifica
                    </Button>
                    {u.id !== currentUser?.id && (
                      <Button size="sm" variant="outline" onClick={() => setDeleteTarget(u)} data-testid={`delete-user-${u.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ) },
            ]}
          />
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifica {editing?.name}</DialogTitle>
            <DialogDescription>Imposta il ruolo e assegna una carta carburante.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ruolo</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="utilizzatore">Utilizzatore</SelectItem>
                  <SelectItem value="approvatore">Approvatore</SelectItem>
                  <SelectItem value="admin">Amministratore (anche approvatore)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Carte carburante</Label>
              <div className="mt-1.5">
                <MultiSelect
                  testid="user-card-select"
                  options={cards.map((c) => ({ value: c.id, label: c.label }))}
                  value={editForm.fuel_card_ids}
                  onChange={(v) => setEditForm({ ...editForm, fuel_card_ids: v })}
                  allLabel="Nessuna carta"
                  placeholder="Cerca carta..."
                />
              </div>
              {cards.length === 0 && (
                <p className="mt-1.5 text-xs text-slate-400">Nessuna carta disponibile. Creale nella sezione "Carte carburante".</p>
              )}
            </div>
            <div>
              <Label>Costo orario per tipo di ore (€)</Label>
              {hourTypes.length === 0 ? (
                <Input id="eu-cost" type="number" step="0.01" data-testid="edit-cost-input" value={editForm.cost_per_hour ?? ""} onChange={(e) => setEditForm({ ...editForm, cost_per_hour: e.target.value })} className="mt-1.5" placeholder="Costo orario di default" />
              ) : (
                <div className="mt-1.5 space-y-2">
                  {hourTypes.map((h) => (
                    <div key={h.id} className="flex items-center gap-2">
                      <span className="w-32 shrink-0 truncate text-sm text-slate-600">{h.name}</span>
                      <Input type="number" step="0.01" data-testid={`edit-cost-${h.id}`} value={editForm.hourly_costs?.[h.id] ?? ""} onChange={(e) => setEditForm({ ...editForm, hourly_costs: { ...editForm.hourly_costs, [h.id]: e.target.value } })} placeholder="0,00" />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Tipo di attività</Label>
              <Select value={editForm.activity_type_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, activity_type_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="edit-activity-select"><SelectValue placeholder="Nessuna attività" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna attività</SelectItem>
                  {activityTypes.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`edit-activity-option-${a.id}`}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
              <div className="pr-4">
                <Label className="text-sm">Genera bozza rapportino dal carburante</Label>
                <p className="mt-0.5 text-xs text-slate-400">Se attivo, le richieste carburante di questo dipendente creano una bozza di rapportino.</p>
              </div>
              <Switch checked={editForm.fuel_creates_rapportino !== false} onCheckedChange={(v) => setEditForm({ ...editForm, fuel_creates_rapportino: v })} data-testid="edit-fuel-draft-switch" />
            </div>
            <div>
              <Label htmlFor="eu-pass">Nuova password</Label>
              <Input
                id="eu-pass"
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                data-testid="edit-password-input"
                value={editForm.password || ""}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                className="mt-1.5"
                placeholder="Lascia vuoto per non modificarla"
              />
              <p className="mt-1.5 text-xs text-slate-400">Compila solo se vuoi reimpostare la password dell'utente (min. 6 caratteri).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={saveEdit} data-testid="save-user-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminare {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              L'utente {deleteTarget?.email} verrà rimosso definitivamente. Le richieste già inviate restano nello storico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} data-testid="confirm-delete-user-btn" className="bg-rose-600 text-white hover:bg-rose-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
